/**
 * The shape of every `policy_config` value, stated once (ADR-0061, M10).
 *
 * ## Why the shapes are here
 *
 * Until M10 a policy value was written only by `pnpm load:reference`, and it checked two
 * things: that the key is one of twelve somebody reads, and that the value is a JSON object.
 * The rest was each reader's business, and every reader handles a wrong shape the same way —
 * **it fails closed and says nothing**. `delivery_attempts_max` stored as `{ "value": "3" }`
 * gives `loadShipmentPolicy` no N, so every re-attempt guard refuses; `{ "Seconds": 21600 }`
 * starts no sub-budget clock. Nothing errors, and the operator was told the value was set.
 *
 * A screen makes that mistake easier to make, not harder. So the shape each reader reads is
 * stated here, and both writers — the loader and `PATCH /ops/config/policy/:key` — refuse
 * exactly what it refuses. That is ADR-0060 §5's rule for `budgetSecondsProblem` applied to
 * twelve values instead of one: **one predicate**. Not one message — the loader prints a
 * sentence and the route answers with `{ path, code }` details the console translates, because
 * no error message a client receives is built from request data (§4).
 *
 * ## Why the key list moved with them
 *
 * `KNOWN_POLICY_KEYS` lived in `packages/db`, and its docblock argued a key is a *name*: one
 * list maintained by hand costs a line when a reader is added. That is still true of a name.
 * It stopped being the whole story when every name acquired a shape, because a map of shapes
 * keyed by a list in another package is two lists — and the `satisfies` clause below can only
 * hold them together when both are in reach of one compiler. `@tard/db` re-exports the list,
 * so nothing that imported it from there moves.
 *
 * ## Written from the reader, and never wider than it
 *
 * A schema **narrower** than its reader refuses a value the platform would have honoured:
 * visible, and cheap to widen. A schema **wider** than its reader accepts a value the guard
 * then reads as absent — the silent failure this file exists to end. So every schema names
 * the reader it was copied from, and where the two differ the schema is the narrower one on
 * purpose: `status` is an enum where `loadRemittanceRail` accepts any string, because a
 * typo'd status reads to `remittanceRailAvailable` as a rail that is not accepting runs.
 *
 * Every object is `.strict()`, for `tariff.ts`'s reason: a field nothing reads is a control
 * that is not there, and a screen that accepted it would say it had been set.
 */
import { z } from 'zod';
import { isCalendarDay } from './geography.js';
import { renderedText } from './rendered-text.js';
import { VAT_COUNTRIES } from './vat.js';
/**
 * Every `policy_config` key with a reader. Any other key is refused by both writers.
 *
 * Nine are inserted by migrations rather than by the seeder (`sealed_shipments_max` by 0011,
 * `courier_silence_escalation` by 0013, `cod_custody_licence` by 0051, `vat_registration` by 0057,
 * `dropoff_proximity` by 0059, `screening_validity` by 0060, `delivery_attempt_spacing` by 0061,
 * `roster_horizon` by 0062 and `courier_shortfall_threshold` by 0063), which is why the seeder's own
 * row list is not the source of truth for this set.
 *
 * **`sb_a_budget`, `sb_b_budget` and `return_sla_budget` left in P2-3** (2026-09-30): each service
 * class carries its own sub-budgets and return promise (migration 0054), because a platform-wide
 * number divided a 48-hour promise by the same hours as a 24-hour one.
 *
 * **`sealed_shipments_max` had no production reader until 2026-09-30.** Migration 0011 seeded it
 * for ADR-0035's pre-positioning bound and nothing read it, while the ADR said it capped
 * residency. The working set now reads it (P2-1): it is how many sealed dropoffs one Courier's
 * handset is sent at once.
 */
export const KNOWN_POLICY_KEYS = [
    'delivery_attempts_max',
    'delivery_attempt_spacing',
    'pickup_retries_max',
    'dropoff_proximity',
    'roster_horizon',
    'cod_tender_tolerance',
    'courier_shortfall_threshold',
    'rto_fee_terms',
    'remittance_rail',
    'visibility_grace_window',
    'visibility_device_ttl',
    'courier_silence_escalation',
    'sealed_shipments_max',
    'cod_custody_licence',
    'vat_registration',
    'screening_validity',
];
export function isKnownPolicyKey(key) {
    return KNOWN_POLICY_KEYS.includes(key);
}
const positiveInteger = z.number().int().min(1);
const nonEmpty = z.string().trim().min(1);
/** `loadShipmentPolicy` and `loadCourierPolicy` read `value` with `Number.isInteger`. */
const atLeastOne = z.object({ value: positiveInteger }).strict();
/** The licence's two states (ADR-0077). Only `granted` lets a live Merchant's COD Order through. */
export const COD_CUSTODY_LICENCE_STATUSES = ['pending', 'granted'];
/**
 * The reasons a change to the licence is recorded under (ADR-0077 §4), which the console says in
 * words: what happened to the licence when its value moved; `value_changed` for a stored record
 * corrected with no grant on either side; `ownership_only` when only the owner or review date moved.
 */
export const COD_CUSTODY_LICENCE_REASONS = [
    'licence_granted',
    'licence_withdrawn',
    'licence_amended',
    'value_changed',
    'ownership_only',
];
/**
 * The reasons a change to the remittance rail is recorded under (ADR-0057, P2-1), which the console
 * says in words. The rail decides two things about the platform's own conduct, and the reason names
 * the one that moved: whether a run may execute without the reference of a transfer a person made
 * (`movesMoney`), then whether runs are taken at all (`status`). `value_changed` when neither moved;
 * `ownership_only` when only the owner or the review date did.
 */
/** Whether the platform is registered for VAT, and so may issue a tax invoice (ADR-0082). */
export const VAT_REGISTRATION_STATUSES = ['unregistered', 'registered'];
/**
 * The reasons a change to the VAT registration is recorded under (ADR-0082 §5), which the console
 * says in words: registered, withdrawn, or the VAT number changed while registered, which is the
 * one field a tax authority reads first. `value_changed` for anything else, `ownership_only` when
 * only the owner or review date moved.
 */
export const VAT_REGISTRATION_REASONS = [
    'registration_recorded',
    'registration_withdrawn',
    'vat_number_changed',
    'value_changed',
    'ownership_only',
];
/**
 * What a change to how long a screening stays current did (P2-8b). A longer window comes first,
 * because it is the loosening a bank asks about: it lets an older screening stand.
 */
export const SCREENING_VALIDITY_REASONS = [
    'validity_lengthened',
    'validity_shortened',
    'lead_changed',
    'value_changed',
    'ownership_only',
];
export const REMITTANCE_RAIL_REASONS = [
    'moves_money_declared',
    'moves_money_withdrawn',
    'rail_frozen',
    'rail_reopened',
    'value_changed',
    'ownership_only',
];
export const POLICY_VALUE_SCHEMAS = {
    /** A limit of zero attempts would refuse every delivery at its first knock. */
    delivery_attempts_max: atLeastOne,
    /**
     * Zero is lawful here and only here: *never re-offer a failed pickup* is a policy a carrier
     * can hold, where zero delivery attempts is not.
     */
    pickup_retries_max: z.object({ value: z.number().int().min(0) }).strict(),
    /**
     * `loadShipmentPolicy` reads `metres` with `Number.isInteger`, and the geofence on arriving at the
     * door (`leg.delivery.arrive`) allows a geo stamp that far from the pin (P2-8a). Narrower than the reader on
     * purpose at the top: a radius past five kilometres is no geofence, and a typo of 50000 for 500
     * would switch the check off while reading as set.
     */
    dropoff_proximity: z.object({ metres: z.number().int().min(1).max(5000) }).strict(),
    /**
     * `loadShipmentPolicy` reads `minutes` with `Number.isInteger`, and the guard on declaring a Buyer
     * unreachable (`attemptsEvidencedWithSpacing`) asks that the last N delivery attempts each came at
     * least this long after the one before (P2-8c, 02a Assumption 3). **Zero is lawful and means no
     * minimum**, stated rather than absent: it is what the platform did before the guard read anything,
     * and what migration 0061 seeds. An absent row is no minimum *known*, and the guard refuses. A day
     * is the top: a gap longer than that would make three attempts outlast any promise this platform
     * makes.
     */
    delivery_attempt_spacing: z.object({ minutes: z.number().int().min(0).max(1440) }).strict(),
    /**
     * How many days ahead a Courier's weekly roster is written out as dated shifts (P2-8d, ADR-0065):
     * `loadRosterHorizon` reads `days`, and both the save and the hourly top-up write that far. **A
     * week at least**, or a weekly roster would not produce every one of its own weekdays; half a year
     * at most, so cancelling a roster never retires more than that, and no holiday decree is assumed
     * known further out. No row means no horizon: a roster is refused and the top-up halts, with
     * `/health` going stale, because writing a guessed horizon is the silent default this table ends.
     */
    roster_horizon: z.object({ days: z.number().int().min(7).max(180) }).strict(),
    /**
     * `loadCourierPolicy` reads `value` with `Number.isInteger` and `>= 1`, and takes anything else
     * as zero: no dropoff pre-positioned. Zero is refused here although the reader would honour it:
     * it switches off the offline opening of every dropoff, which is a decision about I5 rather than
     * a number, and belongs in an ADR if it is ever wanted.
     */
    sealed_shipments_max: atLeastOne,
    /**
     * `loadShipmentPolicy` reads the band for the Order's own COD currency, `band<currency>`, as a
     * non-empty string. The regex is the one the four money fields in `orders.ts` share, message and
     * all: it is minor units, and I2 admits no JSON number for money — not even for a tolerance.
     *
     * **One band per currency the platform prices** (P2-2, 2026-09-30). A single `bandMinor` meant
     * $10 on a USD Order and 1,000 pounds on an LBP one, because USD counts cents and LBP and SYP
     * count whole pounds (`MINOR_UNIT_EXPONENT`). Every currency is required: a band left out would
     * refuse every delivery of an Order in it, and the screen should say so before a Courier does.
     */
    cod_tender_tolerance: z
        .object({
        bandUSD: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
        bandLBP: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
        bandSYP: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
    })
        .strict(),
    /**
     * How large a count's shortfall Hub finance may charge to the Courier without an adjudicator (P2-8e,
     * 02a Machine 2 amended): one band per currency, in that currency's minor units, read by
     * `withinShortfallThreshold`. The **whole count's** shortfall must sit within the band in every
     * currency it touches, so a large shortfall spread over many Orders is not charged a piece at a time.
     * Migration 0063 seeds every band at 0, which is what the platform did before: every shortfall goes
     * to an adjudicator. A currency with no band, and an absent or malformed row, refuse the same way.
     */
    courier_shortfall_threshold: z
        .object({
        bandUSD: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
        bandLBP: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
        bandSYP: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
    })
        .strict(),
    /** `loadShipmentPolicy` reads `version`. The amount is on the Order (migration 0009). */
    rto_fee_terms: z.object({ version: nonEmpty }).strict(),
    /**
     * `loadRemittanceRail` reads `rail` and `status` as strings and `movesMoney === true`.
     *
     * **`movesMoney` is optional, and that is the reader's rule rather than a relaxation.** The
     * rows on every deployed box predate the field and the reader treats absence as `false` —
     * ADR-0057's fail-safe direction. Requiring it would mark the one row this key has as
     * malformed. The screen writes it explicitly every time; the loader may omit it and mean
     * exactly what the reader already takes it to mean.
     *
     * `corridor` is read by nothing and required anyway, because every stored row carries it and
     * a rail with no corridor is not one an operator can reason about.
     */
    remittance_rail: z
        .object({
        rail: nonEmpty,
        status: z.enum(['available', 'frozen']),
        corridor: nonEmpty,
        movesMoney: z.boolean().optional(),
    })
        .strict(),
    /** `loadCourierPolicy` reads `minutes` with `Number.isInteger`. */
    visibility_grace_window: z.object({ minutes: positiveInteger }).strict(),
    /** `loadCourierPolicy` reads `hours` with `Number.isInteger`. */
    visibility_device_ttl: z.object({ hours: positiveInteger }).strict(),
    /**
     * The dark-device board reads both as numbers and renders no bands if either is missing.
     * Red at or below amber is refused although the board would draw it: a board that goes red
     * before it goes amber has no amber, and says so to nobody.
     */
    courier_silence_escalation: z
        .object({ amberMinutes: positiveInteger, redMinutes: positiveInteger })
        .strict()
        .refine((value) => value.redMinutes > value.amberMinutes, {
        message: 'redMinutes must be above amberMinutes',
        path: ['redMinutes'],
    }),
    /**
     * Whether the platform holds the licence to take custody of Buyers' cash (ADR-0077). Counsel
     * answered Q3 on 2026-09-29: it needs one. `loadCodCustodyLicence` reads `status`, and only
     * `granted` lets a COD Order from a Merchant that is not a sandbox one through.
     *
     * **A licence granted names what was granted**: the reference the regulator issued and the day.
     * "Granted" with nothing to point at is an assertion nobody could check. **One pending names
     * neither**, so a reference cannot sit on a row that says no licence is held. Anything this
     * schema refuses, the reader reads as `pending`: the fail-closed direction, as for every key.
     */
    cod_custody_licence: z
        .object({
        status: z.enum(COD_CUSTODY_LICENCE_STATUSES),
        reference: nonEmpty.optional(),
        grantedOn: z.string().refine(isCalendarDay, 'calendar_day').optional(),
    })
        .strict()
        .superRefine((value, ctx) => {
        const issue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
        if (value.status === 'granted') {
            if (value.reference === undefined)
                issue('reference', 'licence_reference_required');
            if (value.grantedOn === undefined)
                issue('grantedOn', 'licence_granted_on_required');
            return;
        }
        if (value.reference !== undefined)
            issue('reference', 'licence_pending_names_nothing');
        if (value.grantedOn !== undefined)
            issue('grantedOn', 'licence_pending_names_nothing');
    }),
    /**
     * The platform's own VAT registration (ADR-0082 §5). `loadVatRegistration` reads it, and an
     * invoice that carries tax needs a registration **for the Merchant's country**: a tax invoice
     * without the supplier's VAT number is not one. Anything this schema refuses reads as
     * unregistered, so a malformed row stops a taxed invoice rather than issuing one naming nobody.
     *
     * **A registration names all four facts, and "unregistered" names none**, the licence's rule:
     * a VAT number on a row that says no registration is held is a claim nobody made.
     */
    vat_registration: z
        .object({
        status: z.enum(VAT_REGISTRATION_STATUSES),
        country: z.enum(VAT_COUNTRIES).optional(),
        legalName: z.string().trim().min(1).max(200).optional(),
        vatNumber: z.string().trim().min(1).max(50).optional(),
        address: z.string().trim().min(1).max(500).optional(),
    })
        .strict()
        .superRefine((value, ctx) => {
        const issue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
        const fields = ['country', 'legalName', 'vatNumber', 'address'];
        if (value.status === 'registered') {
            for (const field of fields) {
                if (value[field] === undefined)
                    issue(field, 'registration_field_required');
            }
            return;
        }
        for (const field of fields) {
            if (value[field] !== undefined)
                issue(field, 'unregistered_names_nothing');
        }
    }),
    /**
     * How long a screening stays current, and how long before it lapses the cadence screens again
     * (P2-8b, §7). `screeningIsCurrent` refuses acceptance and payout once a Merchant's screening is
     * older than `validForDays`, and the cadence picks up a Merchant `leadDays` before that. Both were
     * constants until 2026-10-02: 90 and 7 days. `loadScreeningValidity` reads anything this schema
     * refuses as no window at all, and no screening is then current — the fail-closed direction.
     *
     * **The lead is shorter than the validity**, or the cadence would screen a Merchant whose
     * screening is still new, on every tick. A year is the longest window, because §7 asks for a
     * recurring schedule and a longer one is not one.
     */
    screening_validity: z
        .object({
        validForDays: z.number().int().min(1).max(365),
        leadDays: z.number().int().min(1).max(60),
    })
        .strict()
        .refine((value) => value.leadDays < value.validForDays, {
        message: 'lead_within_validity',
        path: ['leadDays'],
    }),
};
/**
 * Why a value is not the shape its reader reads, as a sentence — or `undefined` when it is.
 *
 * A sentence rather than a throw, so the loader can raise a `ReferenceLoadError` with it and
 * the route can log it. **It does not reach a screen**: the route sends `policyValueIssues`
 * instead, for the reason that function's docblock gives.
 */
export function policyValueProblem(key, value) {
    const schema = POLICY_VALUE_SCHEMAS[key];
    const parsed = schema.safeParse(value);
    if (parsed.success)
        return undefined;
    const issue = parsed.error.issues[0];
    const at = issue && issue.path.length > 0 ? ` at ${issue.path.join('.')}` : '';
    return `value${at} — ${issue?.message ?? 'is not the shape its reader reads'}`;
}
/**
 * The same refusal as structured details — `path` and `code` only — for an HTTP response.
 *
 * **Not the sentence, and that is §4 rather than taste.** The error table is frozen so that no
 * message a client receives is built from request data, and `ApiProblem`'s note is
 * server-side only. So a route cannot send `policyValueProblem`'s words; it sends what the
 * exception filter's zod branch already sends for a body — the path and zod's closed issue
 * code, never the offending value — and the console translates the code. The loader keeps the
 * sentence, because a terminal has no filter between it and the operator.
 */
export function policyValueIssues(key, value) {
    const schema = POLICY_VALUE_SCHEMAS[key];
    const parsed = schema.safeParse(value);
    if (parsed.success)
        return [];
    return parsed.error.issues.map((issue) => ({
        path: ['value', ...issue.path].join('.'),
        code: issue.code,
    }));
}
/** The parser's own output, so a stored value is exactly what the schema returned. */
export function parsePolicyValue(key, value) {
    const schema = POLICY_VALUE_SCHEMAS[key];
    return schema.parse(value);
}
/**
 * The reason a policy value's change is recorded under, derived from its consequence (ADR-0060),
 * so one query over `config.policy_config.amended` answers the question each key raises. Asked only
 * when the value moved: an owner or review date alone is the caller's `ownership_only`. `before` is
 * the stored value, which may be malformed; `after` is the parsed value about to be written.
 *
 * - **The licence** (ADR-0077): granted, withdrawn, or amended while held, by its `status` — "when
 *   was the licence granted, and was it ever withdrawn?", the question a bank asks first.
 * - **The VAT registration** (ADR-0082): recorded, withdrawn, or its VAT number changed while held
 *   — "which number was on the invoices issued in March?" is then one query.
 * - **The rail** (ADR-0057): read as `loadRemittanceRail` reads it. `movesMoney` counts only as
 *   `=== true`, so an absent flag written out as `false` changes nothing; and runs are taken only
 *   when `rail` is a string and `status` is `available`. The flag outranks the status, because it
 *   decides whether the platform may record a payment nobody made; "when did we stop asking for a
 *   transfer reference?" is then one query.
 * - **Every other key**: `value_changed`.
 */
export function policyValueReason(key, before, after) {
    const was = before !== null && typeof before === 'object' ? before : {};
    if (key === 'cod_custody_licence') {
        const wasGranted = was['status'] === 'granted';
        const isGranted = after['status'] === 'granted';
        if (isGranted && !wasGranted)
            return 'licence_granted';
        if (wasGranted && !isGranted)
            return 'licence_withdrawn';
        return isGranted ? 'licence_amended' : 'value_changed';
    }
    if (key === 'vat_registration') {
        const wasRegistered = was['status'] === 'registered';
        const isRegistered = after['status'] === 'registered';
        if (isRegistered && !wasRegistered)
            return 'registration_recorded';
        if (wasRegistered && !isRegistered)
            return 'registration_withdrawn';
        if (isRegistered && was['vatNumber'] !== after['vatNumber'])
            return 'vat_number_changed';
        return 'value_changed';
    }
    if (key === 'screening_validity') {
        const wasDays = typeof was['validForDays'] === 'number' ? was['validForDays'] : undefined;
        const isDays = after['validForDays'];
        if (wasDays !== undefined && isDays > wasDays)
            return 'validity_lengthened';
        if (wasDays !== undefined && isDays < wasDays)
            return 'validity_shortened';
        if (wasDays !== undefined && was['leadDays'] !== after['leadDays'])
            return 'lead_changed';
        return 'value_changed';
    }
    if (key === 'remittance_rail') {
        const movedMoney = was['movesMoney'] === true;
        const movesMoney = after['movesMoney'] === true;
        if (movesMoney && !movedMoney)
            return 'moves_money_declared';
        if (movedMoney && !movesMoney)
            return 'moves_money_withdrawn';
        const tookRuns = typeof was['rail'] === 'string' && was['status'] === 'available';
        const takesRuns = typeof after['rail'] === 'string' && after['status'] === 'available';
        if (tookRuns && !takesRuns)
            return 'rail_frozen';
        if (takesRuns && !tookRuns)
            return 'rail_reopened';
        return 'value_changed';
    }
    return 'value_changed';
}
const INTEGER_FIELD = (name) => ({ name, kind: 'integer' });
export const POLICY_FIELDS = {
    delivery_attempts_max: [INTEGER_FIELD('value')],
    delivery_attempt_spacing: [INTEGER_FIELD('minutes')],
    pickup_retries_max: [INTEGER_FIELD('value')],
    dropoff_proximity: [INTEGER_FIELD('metres')],
    roster_horizon: [INTEGER_FIELD('days')],
    sealed_shipments_max: [INTEGER_FIELD('value')],
    cod_tender_tolerance: [
        { name: 'bandUSD', kind: 'minorUnits' },
        { name: 'bandLBP', kind: 'minorUnits' },
        { name: 'bandSYP', kind: 'minorUnits' },
    ],
    courier_shortfall_threshold: [
        { name: 'bandUSD', kind: 'minorUnits' },
        { name: 'bandLBP', kind: 'minorUnits' },
        { name: 'bandSYP', kind: 'minorUnits' },
    ],
    rto_fee_terms: [{ name: 'version', kind: 'text' }],
    remittance_rail: [
        { name: 'rail', kind: 'text' },
        { name: 'status', kind: 'choice', choices: ['available', 'frozen'] },
        { name: 'corridor', kind: 'text' },
        { name: 'movesMoney', kind: 'boolean', optional: true },
    ],
    visibility_grace_window: [INTEGER_FIELD('minutes')],
    visibility_device_ttl: [INTEGER_FIELD('hours')],
    courier_silence_escalation: [INTEGER_FIELD('amberMinutes'), INTEGER_FIELD('redMinutes')],
    cod_custody_licence: [
        { name: 'status', kind: 'choice', choices: COD_CUSTODY_LICENCE_STATUSES },
        { name: 'reference', kind: 'text', optional: true },
        { name: 'grantedOn', kind: 'text', optional: true },
    ],
    vat_registration: [
        { name: 'status', kind: 'choice', choices: VAT_REGISTRATION_STATUSES },
        { name: 'country', kind: 'choice', optional: true, choices: VAT_COUNTRIES },
        { name: 'legalName', kind: 'text', optional: true },
        { name: 'vatNumber', kind: 'text', optional: true },
        { name: 'address', kind: 'text', optional: true },
    ],
    screening_validity: [INTEGER_FIELD('validForDays'), INTEGER_FIELD('leadDays')],
};
/**
 * The facet that decides a policy key, for the keys `policy` does not (ADR-0066, ADR-0077).
 *
 * Each has a route of its own under that facet, and the policy route refuses it with
 * `facet_is_finance` or `facet_is_compliance`; the console shows it on the policy page read-only,
 * with a pointer, rather than offering a save the API refuses. The API derives its refusal sets
 * from this map, so the two cannot disagree: a key the console treats as `policy`'s while the
 * API refuses it is a save that always fails, and the reverse is a boundary on one screen only.
 */
export const POLICY_KEY_FACETS = {
    remittance_rail: 'finance',
    cod_custody_licence: 'compliance',
    // The platform's VAT registration (ADR-0082): the rules money moves under, as the rates are.
    vat_registration: 'finance',
    // How long a screening stays current (P2-8b): the window I7's forcing function runs on.
    screening_validity: 'compliance',
};
/** The keys one facet decides on a route of its own. */
export function policyKeysDecidedBy(facet) {
    return KNOWN_POLICY_KEYS.filter((key) => POLICY_KEY_FACETS[key] === facet);
}
/**
 * Amending one policy value, and who owns it.
 *
 * `value` is `unknown` here and parsed against its key's schema by the service, because which
 * schema applies depends on the path parameter — a body schema cannot know it. `owner` and
 * `reviewDate` are required for `amendServiceClassSchema`'s reason: both columns are NOT NULL
 * with no default, and a writer that defaulted them could produce a row owned by nobody (§7).
 */
export const amendPolicySchema = z
    .object({
    value: z.unknown(),
    owner: z.string().trim().min(1).max(200),
    reviewDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'review_date_format'),
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const amendPolicyRendered = renderedText(amendPolicySchema, {
    prose: ['owner'],
    // The policy list renders `value` as stored. Its strings are codes, a version, minor units, and
    // the platform's VAT registration copied from its certificate: a legal name, a VAT number and an
    // address, which every taxed invoice shows (ADR-0082 §5). A reference's check is the response
    // scan's, so nothing stored here can make an invoice that cannot be shown.
    refs: ['value'],
    exempt: {
        reviewDate: 'a YYYY-MM-DD date (review_date_format), which holds no phone-shaped digit run',
        note: 'stored only in the config.policy_config.amended event payload; no view renders it',
    },
});
//# sourceMappingURL=policy.js.map